<template>
  <div class="flex flex-row w-screen h-screen relative bg-gray-50">
    <BaseSidebar />
    <div class="h-full w-full flex flex-col flex-1 overflow-y-scroll">
      <div class="border-b w-full flex p-4 justify-between">
        <div class="flex gap-x-1">
          <span
            class="flex items-center cursor-pointer"
            @click="navigateTo({ name: 'contacts' })"
          >
            Contacts
          </span>
          <span class="flex items-center">
            <Icon name="ic:baseline-keyboard-arrow-right" />
          </span>
          <span class="flex items-center text-primary-500 font-bold">
            {{ contactData?.firstName }} {{ contactData?.lastName }}
          </span>
        </div>

        <slot name="createNoteAndTaskButtons">
          <Button
            type="button"
            label="Quick Actions"
            @click="togglePopover"
          >
            <template #icon>
              <Icon name="mdi:dots-vertical" />
            </template>
          </Button>

          <Popover
            ref="popoverRef"
          >
            <div class="w-64 py-2">
              <!-- Communication Section -->
              <div class="px-3 py-2">
                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Communication</span>
              </div>
              <div class="flex flex-col">
                <button
                  v-if="contactData?.email"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  @click="triggerAction('sendEmail')"
                >
                  <Icon
                    name="mdi:email-outline"
                    class="w-5 h-5 text-gray-500"
                  />
                  <span class="flex-1 text-left">Send Email</span>
                </button>

                <button
                  v-if="contactData?.phone"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer w-full"
                  @click="triggerAction('sendSMS')"
                >
                  <Icon
                    name="mdi:message-text-outline"
                    class="w-5 h-5 text-gray-500"
                  />
                  <span class="flex-1 text-left">Send SMS</span>
                </button>
              </div>

              <!-- Divider -->
              <div class="border-t border-gray-100 my-1" />

              <!-- Actions Section -->
              <div class="px-3 py-2">
                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</span>
              </div>
              <div class="flex flex-col">
                <button
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  @click="triggerAction('noteCreate')"
                >
                  <Icon
                    name="mdi:note-plus-outline"
                    class="w-5 h-5 text-gray-500"
                  />
                  <span class="flex-1 text-left">Create Note</span>
                </button>
                <button
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  @click="triggerAction('taskCreate')"
                >
                  <Icon
                    name="mdi:checkbox-marked-circle-plus-outline"
                    class="w-5 h-5 text-gray-500"
                  />
                  <span class="flex-1 text-left">Create Task</span>
                </button>
                <button
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  @click="triggerAction('addToTargetList')"
                >
                  <Icon
                    name="mdi:target"
                    class="w-5 h-5 text-gray-500"
                  />
                  <span class="flex-1 text-left">Add to Target List</span>
                </button>
              </div>
            </div>
          </Popover>

          <div class="hidden">
            <ContactAddToTargetList
              ref="addToTargetListRef"
              :show-button="false"
              :contact="contactData"
              @refresh="eventBus.emit()"
            />

            <ContactSendEmail
              v-if="contactData?.email"
              ref="sendEmailRef"
              :show-button="false"
              :recipient="contactData?.email"
              :contact-id="contactId"
              @refresh="eventBus.emit()"
            />

            <ContactSendSMS
              v-if="contactData?.phone"
              ref="sendSMSRef"
              :show-button="false"
              :recipient="contactData?.phone"
              :contact-id="contactId"
              business-id=""
              @refresh="eventBus.emit()"
            />

            <NoteCreate
              ref="noteCreateRef"
              v-model:contact-id="contactId"
              :show-button="false"
              @refresh="eventBus.emit()"
            />

            <TaskCreate
              ref="taskCreateRef"
              v-model:contact-id="contactId"
              :show-button="false"
              @refresh="eventBus.emit()"
            />
          </div>
        </slot>
      </div>

      <div
        v-if="!isLoading"
        class="flex flex-row w-full h-full overflow-y-hidden"
      >
        <ContactDetailSideBar
          v-model:contact="contactData"
        />

        <div
          class="h-full w-full flex flex-col flex-1 bg-gray-100 overflow-y-auto"
        >
          <div class="p-6 rounded-md contentContainer">
            <slot />
          </div>
        </div>
      </div>
      <div
        v-else
        class="w-full flex items-center justify-center"
      >
        <ProgressSpinner />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useEventBus } from '@vueuse/core'

const eventBus = useEventBus<void>('refresh')

const route = useRoute()
const contactId = computed(() => {
  return route.params?.contactId?.toString()
})
const contactData = ref()
const isLoading = ref(true)
const { fetchContactById } = useContacts()
onMounted(async () => {
  await loadContact()
})
watch(contactId, async () => {
  await loadContact()
})

const popoverRef = ref()
const togglePopover = (event: Event) => {
  popoverRef.value.toggle(event)
}

// Action component refs
const addToTargetListRef = ref()
const sendEmailRef = ref()
const sendSMSRef = ref()
const noteCreateRef = ref()
const taskCreateRef = ref()

const triggerAction = (action: 'sendEmail' | 'sendSMS' | 'noteCreate' | 'taskCreate' | 'addToTargetList') => {
  if (!action) return
  if (action === 'sendEmail') {
    sendEmailRef.value.toggleOpen()
  }
  else if (action === 'sendSMS') {
    sendSMSRef.value.toggleOpen()
  }
  else if (action === 'noteCreate') {
    noteCreateRef.value.toggleOpen()
  }
  else if (action === 'taskCreate') {
    taskCreateRef.value.toggleOpen()
  }
  else if (action === 'addToTargetList') {
    addToTargetListRef.value.toggleOpen()
  }
}

const loadContact = async () => {
  isLoading.value = true
  const res = await fetchContactById(contactId.value)
  contactData.value = res.data
  isLoading.value = false
}
</script>
